import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [download, setDownload] = useState(null); // {productTitle, token}

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`, { baseURL: process.env.REACT_APP_BACKEND_URL });
        if (res.data.payment_status === "paid") {
          setState("paid");
          try {
            const pur = await api.get(`/downloads/purchase/${sessionId}`);
            if (pur.data.ready) setDownload({ productTitle: pur.data.productTitle, token: pur.data.token });
          } catch (e) {}
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) { setState("failed"); return; }
      } catch (e) {}
      if (tries++ < 8) setTimeout(poll, 2000); else setState("failed");
    };
    poll();
  }, [sessionId]);

  const dlUrl = download ? `${process.env.REACT_APP_BACKEND_URL}/api/v1/downloads/access/${download.token}` : null;

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="payment-result">
          {state === "checking" && <><Loader2 className="h-14 w-14 text-electric mx-auto animate-spin" /><h1 className="font-heading text-xl font-bold text-navy mt-4">Confirming your payment…</h1><p className="text-slate-500 mt-1 text-sm">This only takes a moment.</p></>}
          {state === "paid" && <>
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-navy mt-4">Payment successful!</h1>
            {download ? (
              <>
                <p className="text-slate-600 mt-2">Your download <b>{download.productTitle}</b> is ready. Link valid for 30 days and saved in your portal.</p>
                <a href={dlUrl} target="_blank" rel="noreferrer"><Button className="mt-5 w-full bg-electric hover:bg-tech gap-2" data-testid="download-now-btn"><Download className="h-4 w-4" /> Download PDF</Button></a>
              </>
            ) : (
              <p className="text-slate-600 mt-2">Thank you. A receipt is available from your invoice lookup or customer portal.</p>
            )}
          </>}
          {state === "failed" && <><XCircle className="h-14 w-14 text-amber-500 mx-auto" /><h1 className="font-heading text-xl font-bold text-navy mt-4">We couldn't confirm payment</h1><p className="text-slate-600 mt-2 text-sm">If you completed checkout, it may take a moment to reflect. Try again shortly.</p></>}
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/digital-downloads"><Button variant="outline">More Downloads</Button></Link>
            <Link to="/"><Button className="bg-electric hover:bg-tech">Home</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
